import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
// Removed useNavigate import
import comms from '../../comms';
import { initializeSocket, formatPhone } from './socketHelper';
import WaypointModal from './TripModal';
import Itinerary from './Itinerary';
import GeolocationHandler from './GeolocationHandler';
import {
  normalizeMapLatLng,
  isValidMapPosition,
  readGoogleLatLng,
  jobLegToLocationArray,
  waypointAddressFromLeg,
  PASSENGER_DETERMINED_DROP,
} from './locationHelper';
import GpsWarningTag from './GpsWarningTag';
import WaypointMap from './WaypointMap';
import Messages from './Messages';
import useSoftMeter from '../../../hooks/useSoftMeter';
import { parseMeterConfig } from '../../../services/softMeter';
import { extractJobPricing } from '../../../utils/extractJobPricing';
import FareConfirmationModal from './FareConfirmationModal';
import { configureOsrm } from '../../../services/osrmMatch';
import { getDirections } from '../../../services/directionsService';

/** Normalize trip/job id so 55644 and "55644" share one pricing bucket. */
function tripKey(id) {
  if (id === null || id === undefined || id === '') return '';
  return String(id).trim();
}

/** Normalize waypoint type for sort / filters (server may use different casing). */
function waypointTypeKey(t) {
  return String(t || '').trim().toLowerCase();
}

/**
 * Sort stops for the driver: same trip → Pickup before Dropoff (bad ETAs often invert this);
 * different trips → by ETA.
 */
function sortWaypointsDrivingOrder(waypoints) {
  const typeRank = (t) => {
    const k = waypointTypeKey(t);
    if (k === 'pickup') return 0;
    if (k === 'break') return 1;
    if (k === 'dropoff') return 2;
    return 99;
  };
  return [...waypoints].sort((a, b) => {
    const ja = tripKey(a.jobNumber);
    const jb = tripKey(b.jobNumber);
    if (ja !== jb) {
      return (a.timeStamp || 0) - (b.timeStamp || 0);
    }
    const oa = typeRank(a.type);
    const ob = typeRank(b.type);
    if (oa !== ob) return oa - ob;
    return (a.timeStamp || 0) - (b.timeStamp || 0);
  });
}

/** True if raw meter JSON parses to something that can bill (not an empty stub). */
function hasUsableMeterRates(rawMeter) {
  const cfg = parseMeterConfig(rawMeter);
  if (!cfg) return false;
  return (
    cfg.flagRate > 0 ||
    cfg.distancePrice > 0 ||
    (cfg.distanceBlockMiles > 0 && cfg.distanceBlockRate > 0) ||
    cfg.timeUnitRate > 0 ||
    cfg.minimumFare > 0
  );
}

/** Statuses that mean "trip is in progress" — Flag Trip is hidden while any of these holds. */
const IN_PROGRESS_TRIP_STATUSES = new Set([
  'dispatched',
  'enroute',
  'arrivedorigin',
  'loaded',
  'arriveddestination',
]);

function tripIsInProgress(trip) {
  if (!trip) return false;
  const s = String(trip.status || '').trim().toLowerCase();
  return IN_PROGRESS_TRIP_STATUSES.has(s);
}

/** Keys fleet APIs may use for street / flag trip (camelCase, PascalCase, etc.). */
const ALLOW_FLAG_FLEET_KEYS = [
  'allowFlag',
  'AllowFlag',
  'allow_flag',
  'enableFlagTrip',
  'EnableFlagTrip',
  'flagPassenger',
  'FlagPassenger',
];

function firstDefinedFleetValue(settings, keys) {
  if (!settings || typeof settings !== 'object') return undefined;
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(settings, k)) continue;
    const v = settings[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function coerceFleetBoolean(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes' || s === 'y') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === 'n') return false;
  }
  return false;
}

/**
 * Whether to show Flag Trip in LiveDispatch. Older APIs omit allowFlag entirely — we default ON
 * so the control is visible unless the fleet explicitly disables it. Override with
 * REACT_APP_ALLOW_FLAG_TRIP=true|false.
 */
function fleetAllowsFlagTrip(settings) {
  const env = String(process.env.REACT_APP_ALLOW_FLAG_TRIP || '')
    .trim()
    .toLowerCase();
  if (env === 'false' || env === '0' || env === 'no') return false;
  if (env === 'true' || env === '1' || env === 'yes') return true;
  const raw = firstDefinedFleetValue(settings, ALLOW_FLAG_FLEET_KEYS);
  if (raw === undefined) return true;
  return coerceFleetBoolean(raw);
}

function LiveDispatch({
  navigateTo,
  userSettings,
  fleetSettings,
  socket,
  driverData,
  googleMapsLoaded = false,
}) {
  // Removed navigate
  const [jobDetails, setJobDetails] = useState([]);
  const jobDetailsRef = useRef([]);
  useEffect(() => {
    jobDetailsRef.current = jobDetails;
  }, [jobDetails]);

  const [driverDetails, setDriverDetails] = useState(null);
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const mapContainerRef = useRef(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [center, setCenter] = useState({ lat: 28.05, lng: -82.4483 });
  const [currentEta, setCurrentEta] = useState(null);
  /** { minutes, usesTraffic } from last successful directions (traffic-aware when Google provides it). */
  const [routeDriveSummary, setRouteDriveSummary] = useState(null);
  const isInitialized = useRef(false);
  
  // Vehicle orientation tracking
  const [vehicleOrientation, setVehicleOrientation] = useState(0);
  const previousPosition = useRef(null);
  
  // New state for dispatch functionality — do not hydrate Dispatched/Enroute/Loaded from
  // localStorage alone (after refresh jobDetails is empty until STARTM/JOBM), or the footer
  // shows "Dispatched" while the map says "No Assigned Trips". OffService/Busy/Clear persist.
  const [vehicleStatus, setVehicleStatus] = useState(() => {
    const v = comms.getCurrentVehicleStatus();
    if (v === 'Dispatched' || v === 'Enroute' || v === 'Loaded') return 'Clear';
    return v || 'Clear';
  });
  const [currentTrip, setCurrentTrip] = useState(null);
  const [messages, setMessages] = useState([]);
  const [jobOffers, setJobOffers] = useState([]);
  const [showJobOffer, setShowJobOffer] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ show: false, tripNumber: null });
  const [messageInput, setMessageInput] = useState('');
  

  const [showMessageInput, setShowMessageInput] = useState(false);
  
  // Messages state - moved up to be available immediately
  const [showMessages, setShowMessages] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const flashingInterval = useRef(null);

  // Job offer auto-accept timer tracking
  const jobOfferTimerRef = useRef(null);
  const [offerCountdown, setOfferCountdown] = useState(0);
  const countdownIntervalRef = useRef(null);

  // Toast state for schedule updates
  const [showScheduleToast, setShowScheduleToast] = useState(false);
  const toastCooldownRef = useRef(false);
  // Session-local map endpoint override used only while SoftMeter runs.
  const [softMeterRouteOverride, setSoftMeterRouteOverride] = useState(null);
  const activeMeterTripKeyRef = useRef('');

  // SoftMeter — GPS-based fare calculator
  // jobNumber → { pricing, meter, wait, disposition } from server job data
  const tripPricingRef = useRef({});

  /** Miles/mph by default; km/kmh when job or fleet sets a known metric flag. */
  const meterIsMetric = useMemo(() => {
    const job = jobDetails[0];
    const jobHint = firstDefinedFleetValue(job || {}, [
      'useMetric',
      'UseMetric',
      'isMetric',
      'IsMetric',
    ]);
    const fleetHint = firstDefinedFleetValue(fleetSettings || {}, [
      'useMetric',
      'UseMetric',
      'isMetric',
      'IsMetric',
      'metricDistance',
      'MetricDistance',
    ]);
    const raw =
      jobHint !== undefined && jobHint !== null && jobHint !== '' ? jobHint : fleetHint;
    if (raw === undefined || raw === null || raw === '') return false;
    return coerceFleetBoolean(raw);
  }, [jobDetails, fleetSettings]);

  const {
    fare: meterFare,
    distance: meterDistance,
    fareMode: meterFareMode,
    isRunning: meterRunning,
    meterConfig: meterMeterConfig,
    elapsedTime: meterElapsedTime,
    fareTimeSeconds: meterFareTimeSeconds,
    waitTime: meterWaitTime,
    waitCharge: meterWaitCharge,
    distanceCharge: meterDistanceCharge,
    timeCharge: meterTimeCharge,
    startMeter,
    stopMeter,
    pauseMeter,
    reEnableMeter,
    onLocationUpdate: meterLocationUpdate,
    calculateFinalFare,
    getFareBreakdown,
    getMeterStats,
    getRunningJobNumber,
  } = useSoftMeter({
    isMetric: meterIsMetric,
    onMeterTick: (stats) => {
      comms.sendMeter(stats);
    },
  });

  const meterRunningRef = useRef(false);
  useEffect(() => {
    meterRunningRef.current = meterRunning;
  }, [meterRunning]);

  // Route edits are session-local: clear when meter stops or active trip switches.
  useEffect(() => {
    const firstTrip = jobDetails[0] ? tripKey(jobDetails[0].jobNumber) : '';
    if (!meterRunning) {
      if (softMeterRouteOverride) setSoftMeterRouteOverride(null);
      activeMeterTripKeyRef.current = '';
      return;
    }
    if (
      activeMeterTripKeyRef.current &&
      firstTrip &&
      activeMeterTripKeyRef.current !== firstTrip &&
      softMeterRouteOverride
    ) {
      setSoftMeterRouteOverride(null);
    }
    activeMeterTripKeyRef.current = firstTrip;
  }, [meterRunning, jobDetails, softMeterRouteOverride]);

  const handleSetSoftMeterRouteOverride = useCallback((override) => {
    if (!meterRunningRef.current) return;
    const origin = normalizeMapLatLng(override?.origin);
    const destination = normalizeMapLatLng(override?.destination);
    if (!origin || !destination) return;
    if (!isValidMapPosition(origin) || !isValidMapPosition(destination)) return;
    setSoftMeterRouteOverride({
      origin,
      destination,
      destinationLabel: String(override?.destinationLabel || '').trim(),
    });
  }, []);

  const handleClearSoftMeterRouteOverride = useCallback(() => {
    setSoftMeterRouteOverride(null);
  }, []);

  /** While Flag Trip passenger-count modal is open, do not auto-start SoftMeter (STARTM/JOBM race). */
  const flagTripModalOpenRef = useRef(false);
  /** Resolver registered by the active FlagTripModal — invoked when JOBMF arrives. */
  const pendingFlagAckRef = useRef(null);

  const onFlagTripModalOpen = useCallback(() => {
    flagTripModalOpenRef.current = true;
  }, []);

  const onFlagTripModalDismiss = useCallback(() => {
    flagTripModalOpenRef.current = false;
    pendingFlagAckRef.current = null;
  }, []);

  /** FlagTripModal calls this with a resolver fn (or null to clear). */
  const registerPendingFlagAck = useCallback((resolver) => {
    pendingFlagAckRef.current = typeof resolver === 'function' ? resolver : null;
  }, []);

  const guardedStartMeter = useCallback(
    (jobNumber, rawMeter, rawWait) => {
      if (flagTripModalOpenRef.current) return;
      startMeter(jobNumber, rawMeter, rawWait);
    },
    [startMeter]
  );

  /**
   * Flag Trip succeeded for a specific jobNumber (arrived via JOBMF). Look up that trip's
   * pricing rather than blindly indexing jobDetails[0] — multiple trips may co-exist.
   *
   * NOTE: do NOT gate on jobDetailsRef.current — onJobDetails calls setJobDetails (async
   * React state) right before onFlagTrip fires the resolver that lands here, so the ref
   * hasn't been updated yet on this tick. tripPricingRef IS updated synchronously inside
   * onJobDetails, so it's safe to read here. JOBMF for a flag trip implies the trip is
   * Loaded by definition, so start the meter unconditionally if rates are usable.
   */
  const handleFlagTripModalClosed = useCallback((jobNumber) => {
    flagTripModalOpenRef.current = false;
    pendingFlagAckRef.current = null;
    if (jobNumber === undefined || jobNumber === null) return;
    const tk = tripKey(jobNumber);
    if (!tk) return;
    const p = tripPricingRef.current[tk];
    if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
      startMeter(jobNumber, p.meter, p.wait);
    }
  }, [startMeter]);

  // Configure OSRM Match service once per mount (fleet-driven base URL).
  useEffect(() => {
    configureOsrm({ fleetSettings });
  }, [fleetSettings]);

  // Fare confirmation modal state (shown before completing a metered trip)
  const [fareConfirmModal, setFareConfirmModal] = useState({ show: false, tripNumber: null, fareData: null });

  // Ref to hold latest socket callbacks — avoids stale closures
  const socketCallbacksRef = useRef({});

  // Track whether we're on a phone (vs. tablet/desktop)
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 768); // e.g. 768px as phone breakpoint
      }
    };
    handleResize(); // Run once on component mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);

  const allowFlag = fleetAllowsFlagTrip(fleetSettings);

  /**
   * Flag Trip is only visible when:
   *  - the fleet allows it,
   *  - there is no in-progress trip (Dispatched/Enroute/ArrivedOrigin/Loaded/ArrivedDestination),
   *  - the meter is not running.
   * Scheduled-but-not-started trips do NOT block flag eligibility.
   */
  const canFlag = !!(
    allowFlag &&
    !meterRunning &&
    !tripIsInProgress(currentTrip)
  );

  // Calculate vehicle orientation based on movement
  const calculateOrientation = (currentPos, previousPos) => {
    if (!previousPos) return 0;
    
    const deltaLat = currentPos.lat - previousPos.lat;
    const deltaLng = currentPos.lng - previousPos.lng;
    
    // Calculate bearing in degrees (0 = North, 90 = East, 180 = South, 270 = West)
    let bearing = Math.atan2(deltaLng, deltaLat) * (180 / Math.PI);
    
    // Normalize to 0-360 degrees
    if (bearing < 0) bearing += 360;
    
    return bearing;
  };

  const handleLocationUpdate = (newLocation) => {
    const normalized = normalizeMapLatLng(newLocation);
    if (!normalized) return;

    // Calculate orientation if we have a previous position
    if (previousPosition.current && isAccurate) {
      const orientation = calculateOrientation(normalized, previousPosition.current);
      setVehicleOrientation(orientation);
    }

    previousPosition.current = { ...normalized };

    // GoogleMap is controlled via `center` only. Do not also call map.panTo — that double-updates
    // the map and races Google's map_changed internals (minified "wE"/"pH" undefined errors).
    setCenter(normalized);
    // GPS sending is now handled automatically by GeolocationHandler
    // No need to send GPS manually here anymore
  };

  // Start flashing animation for messages button - moved up to be available immediately
  const startFlashing = useCallback(() => {
    if (flashingInterval.current) return; // Already flashing
    
    setIsFlashing(true);
    flashingInterval.current = setInterval(() => {
      setIsFlashing(prev => !prev);
    }, 500); // Flash every 500ms
  }, []);

  // Stop flashing animation - moved up to be available immediately
  const stopFlashing = useCallback(() => {
    if (flashingInterval.current) {
      clearInterval(flashingInterval.current);
      flashingInterval.current = null;
    }
    setIsFlashing(false);
    setHasUnreadMessages(false);
  }, []);

  // Clean up flashing interval on unmount
  useEffect(() => {
    return () => {
      if (flashingInterval.current) {
        clearInterval(flashingInterval.current);
      }
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Socket command handlers
  const socketCallbacks = {
    onStart: (status) => {
      setVehicleStatus(status || 'Clear');
      comms.setCurrentVehicleStatus(status || 'Clear');
    },

    /**
     * START with no jobs — clear itinerary. Guard pricing wipe so a stray empty STARTM
     * during a metered trip doesn't drop the rate card mid-fare (Phase 3.12).
     */
    onStartNoJobs: () => {
      setJobDetails([]);
      if (meterRunningRef.current || currentTrip) {
        console.warn(
          'onStartNoJobs received while meter running / currentTrip present — keeping pricing for safety'
        );
        return;
      }
      setCurrentTrip(null);
      comms.setCurrentTripStatus(null);
      setVehicleStatus('Clear');
      comms.setCurrentVehicleStatus('Clear');
      tripPricingRef.current = {};
    },
    
    onStartM: (jobsArray) => {
      try {
        let jobs = JSON.parse(jobsArray);
        if (!Array.isArray(jobs)) {
          jobs = [jobs];
        }

        // Process all waypoints from all jobs
        let allWaypoints = [];
        let tripStatuses = {};
        
        jobs.forEach((rawJob) => {
          const job = {
            ...rawJob,
            info: rawJob.info ?? rawJob.Info,
            locations: rawJob.locations ?? rawJob.Locations,
          };
          if (!job.info || !job.locations) {
            console.warn('STARTM/STARTN job missing info or locations:', rawJob);
            return;
          }

          // Track trip status for vehicle status calculation
          tripStatuses[job.info.jobNumber] = job.info.status;

          const jnKey = tripKey(job.info.jobNumber);
          const { meter, wait, pricing, disposition } = extractJobPricing(job);
          if (meter || wait || pricing.length) {
            tripPricingRef.current[jnKey] = {
              pricing,
              meter: meter || null,
              wait: wait || null,
              disposition,
            };
          }
          
          // Add pickup waypoint only if trip is not already loaded or arrived at destination
          if (job.info.status !== 'Loaded' && job.info.status !== 'ArrivedDestination' && job.info.status !== 'Completed') {
            const pickupAddr = waypointAddressFromLeg(job.locations[0], { isDropoff: false });
            allWaypoints.push({
              type: 'Pickup',
              location: jobLegToLocationArray(job.locations[0]),
              timeStamp: job.locations[0].eta,
              time: new Date(job.locations[0].eta * 1000).toLocaleTimeString(),
              paxName: job.info.name,
              jobNumber: job.info.jobNumber,
              address1: pickupAddr.address1,
              address2: pickupAddr.address2,
              account: job.info.account,
              phone: formatPhone(job.info.phone),
              wheelChair: !!job.info.seatingRequired?.wheelchair,
              completed: false,
              status: job.info.status
            });
          }
          
          // Always add dropoff waypoint (unless trip is completed)
          if (job.info.status !== 'Completed') {
            const dropAddr = waypointAddressFromLeg(job.locations[1], { isDropoff: true });
            allWaypoints.push({
              type: 'Dropoff',
              location: jobLegToLocationArray(job.locations[1]),
              timeStamp: job.locations[1].eta,
              time: new Date(job.locations[1].eta * 1000).toLocaleTimeString(),
              paxName: job.info.name,
              jobNumber: job.info.jobNumber,
              address1: dropAddr.address1,
              address2: dropAddr.address2,
              account: job.info.account,
              phone: formatPhone(job.info.phone),
              wheelChair: !!job.info.seatingRequired?.wheelchair,
              completed: false,
              status: job.info.status
            });
          }
        });

        allWaypoints = sortWaypointsDrivingOrder(allWaypoints);

        // Trips exist in payload but every leg was filtered out (e.g. all Completed) — avoid
        // vehicleStatus "Dispatched" with an empty waypoint list. Guard the pricing wipe so a
        // stale all-completed STARTM never drops the rate card mid-fare (Phase 3.12).
        if (allWaypoints.length === 0 && Object.keys(tripStatuses).length > 0) {
          console.warn(
            'STARTM/STARTN: trips in payload but no waypoints (e.g. all Completed) — clearing UI'
          );
          if (meterRunningRef.current || currentTrip) {
            console.warn(
              'all-completed STARTM with meter running / currentTrip present — keeping pricing for safety'
            );
            return;
          }
          tripPricingRef.current = {};
          setCurrentTrip(null);
          comms.setCurrentTripStatus(null);
          setJobDetails([]);
          setVehicleStatus('Clear');
          comms.setCurrentVehicleStatus('Clear');
          return;
        }

        // Find "active" job - the job with the next waypoint to visit
        const activeJob = allWaypoints.length > 0 ? {
          tripNumber: allWaypoints[0].jobNumber,
          status: allWaypoints[0].status,
          nextEventType: allWaypoints[0].type
        } : null;

        // Determine vehicle status based on trip statuses
        let vehicleStatus = 'Clear';
        
        if (Object.keys(tripStatuses).length === 0) {
          // No trips - status determined separately (Clear or Busy)
          vehicleStatus = 'Clear';
        } else if (Object.keys(tripStatuses).length === 1) {
          // Single trip - status based on trip status
          const tripStatus = Object.values(tripStatuses)[0];
          switch (tripStatus) {
            case 'Assigned':
            case 'ArrivedOrigin':
              vehicleStatus = 'Dispatched';
              break;
            case 'Enroute':
              vehicleStatus = 'Enroute';
              break;
            case 'Loaded':
            case 'ArrivedDestination':
              vehicleStatus = 'Loaded';
              break;
            default:
              vehicleStatus = 'Dispatched';
          }
        } else {
          // Multiple trips - find highest status
          const statusPriority = {
            'Assigned': 1,
            'Enroute': 2,
            'ArrivedOrigin': 3,
            'Loaded': 4,
            'ArrivedDestination': 5,
            'Completed': 6
          };
          
          let highestPriority = 0;
          let highestStatus = 'Clear';
          
          Object.values(tripStatuses).forEach(status => {
            const priority = statusPriority[status] || 0;
            if (priority > highestPriority) {
              highestPriority = priority;
              highestStatus = status;
            }
          });
          
          // Map highest trip status to vehicle status
          switch (highestStatus) {
            case 'Assigned':
            case 'ArrivedOrigin':
            case 'Enroute':
              vehicleStatus = highestStatus === 'Enroute' ? 'Enroute' : 'Dispatched';
              break;
            case 'Loaded':
            case 'ArrivedDestination':
              vehicleStatus = 'Loaded';
              break;
            default:
              vehicleStatus = 'Dispatched';
          }
        }

        setCurrentTrip(activeJob);
        if (activeJob) {
          comms.setCurrentTripStatus(activeJob);
        }
        
        setJobDetails(allWaypoints);
        setVehicleStatus(vehicleStatus);
        comms.setCurrentVehicleStatus(vehicleStatus);

        // Schedule already shows Loaded (e.g. after reconnect) — start meter without a second Pickup tap
        jobs.forEach((rawJob) => {
          const j = {
            ...rawJob,
            info: rawJob.info ?? rawJob.Info,
            locations: rawJob.locations ?? rawJob.Locations,
          };
          if (!j.info || String(j.info.status || '').trim().toLowerCase() !== 'loaded') return;
          const tk = tripKey(j.info.jobNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
            guardedStartMeter(j.info.jobNumber, p.meter, p.wait);
          }
        });
        
      } catch (e) {
        console.error('Error parsing STARTM jobs:', e);
      }
    },
    
    onLogon: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
      }
    },
    
    onLogoff: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('OffService');
        comms.setCurrentVehicleStatus('OffService');
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
      }
    },
    
    onBusy: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('Busy');
        comms.setCurrentVehicleStatus('Busy');
      }
    },
    
    onClear: (result) => {
      if (result === 'Ok') {
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
      }
    },
    
    onPing: () => {
      const c = normalizeMapLatLng(center);
      if (c) comms.sendGPS(c.lat, c.lng);
    },
    
    onMessage: (message) => {
      const newMessage = {
        id: Date.now(),
        text: message,
        timestamp: new Date(),
        type: 'received',
        from: 'Dispatch'
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      // Start flashing if messages modal is not open
      if (!showMessages) {
        setHasUnreadMessages(true);
        startFlashing();
      }
    },
    
    onJobOffer: (tripNumber, offerData) => {
      // driverTripRejection: 0 = auto-accept after 1s, >0 = show UI and auto-accept after X seconds
      const rejectionWindow = fleetSettings?.driverTripRejection || 0;
      const autoAcceptDelay = rejectionWindow > 0 ? rejectionWindow * 1000 : 1000;
      
      // Show offer UI only if driverTripRejection > 0
      if (rejectionWindow > 0) {
        try {
          const offer = typeof offerData === 'string' ? JSON.parse(offerData) : offerData;
          setPendingOffer({ tripNumber, ...offer });
          setShowJobOffer(true);
          
          // Start countdown display
          setOfferCountdown(rejectionWindow);
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = setInterval(() => {
            setOfferCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } catch (e) {
          console.error('Error parsing job offer:', e);
        }
      }
      
      // Schedule update notification
      if (!toastCooldownRef.current && rejectionWindow === 0) {
        setShowScheduleToast(true);
      }
      
      // Set up auto-accept timer — if driver doesn't respond within the window, auto-accept
      if (jobOfferTimerRef.current) clearTimeout(jobOfferTimerRef.current);
      jobOfferTimerRef.current = setTimeout(() => {
        comms.acceptTrip(tripNumber);
        // Close offer UI if open
        setShowJobOffer(false);
        setPendingOffer(null);
        setOfferCountdown(0);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        jobOfferTimerRef.current = null;
      }, autoAcceptDelay);
    },
    
    onJobOfferAutoAccepted: undefined, // No longer needed — auto-accept is handled in onJobOffer
    onJobOfferTimer: undefined, // No longer needed — timer is managed in onJobOffer
    
    onScheduleUpdate: () => {
      // Show toast only if not in cooldown
      if (!toastCooldownRef.current) {
        setShowScheduleToast(true);
      }
    },
    
    onJobDetails: (tripNumber, jobText) => {
      try {
        if (!jobText) {
          console.warn('No job details data received for trip:', tripNumber);
          return;
        }
        const rawJob = typeof jobText === 'string' ? JSON.parse(jobText) : jobText;
        const job = {
          ...rawJob,
          info: rawJob.info ?? rawJob.Info,
          locations: rawJob.locations ?? rawJob.Locations,
        };

        if (!job || !job.info || !job.locations) {
          console.warn('Invalid job details format for trip:', tripNumber);
          return;
        }

        const jnKey = tripKey(job.info.jobNumber);
        const { meter, wait, pricing, disposition } = extractJobPricing(job);
        if (meter || wait || pricing.length) {
          tripPricingRef.current[jnKey] = {
            pricing,
            meter: meter || null,
            wait: wait || null,
            disposition,
          };
        }

        // Phase 3.11 — JOBM for a *different* trip while meter still runs:
        // do not stop or replace the live meter; defer the new trip's meter until completion.
        if (meterRunningRef.current) {
          const runningJn = getRunningJobNumber();
          if (runningJn && jnKey && tripKey(runningJn) !== jnKey) {
            console.error(
              `Meter still running for trip ${runningJn} — settle before starting trip ${job.info.jobNumber}`
            );
          }
        }

        // Build pickup/dropoff waypoints from the job (same logic as onStartM)
        const newWaypoints = [];

        // Add pickup waypoint only if trip is not already loaded or arrived at destination
        if (
          job.info.status !== 'Loaded' &&
          job.info.status !== 'ArrivedDestination' &&
          job.info.status !== 'Completed' &&
          job.locations[0]
        ) {
          const pickupAddr = waypointAddressFromLeg(job.locations[0], { isDropoff: false });
          newWaypoints.push({
            type: 'Pickup',
            location: jobLegToLocationArray(job.locations[0]),
            timeStamp: job.locations[0].eta,
            time: new Date(job.locations[0].eta * 1000).toLocaleTimeString(),
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            address1: pickupAddr.address1,
            address2: pickupAddr.address2,
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        // Add dropoff waypoint (unless trip is completed). Flag trips arrive without a
        // destination — `job.locations[1]` is undefined until dispatch (or the driver)
        // assigns a dropoff, so we skip the dropoff waypoint in that case.
        if (job.info.status !== 'Completed' && job.locations[1]) {
          const dropAddr = waypointAddressFromLeg(job.locations[1], { isDropoff: true });
          newWaypoints.push({
            type: 'Dropoff',
            location: jobLegToLocationArray(job.locations[1]),
            timeStamp: job.locations[1].eta,
            time: new Date(job.locations[1].eta * 1000).toLocaleTimeString(),
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            address1: dropAddr.address1,
            address2: dropAddr.address2,
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        // Flag trip arrives JOBMF as Loaded with only an Origin location — no pickup
        // (already loaded) and no dropoff (passenger-determined). Synthesize a Dropoff
        // waypoint with the "Passenger Determined" placeholder so the trip binds to
        // jobDetails / currentTrip and CurrentWaypoint can display it as Loaded.
        if (
          newWaypoints.length === 0 &&
          job.info.status === 'Loaded' &&
          job.locations[0] &&
          !job.locations[1]
        ) {
          newWaypoints.push({
            type: 'Dropoff',
            location: [0, 0],
            timeStamp: 0,
            time: '',
            paxName: job.info.name,
            jobNumber: job.info.jobNumber,
            address1: PASSENGER_DETERMINED_DROP,
            address2: '',
            account: job.info.account,
            phone: formatPhone(job.info.phone),
            wheelChair: !!job.info.seatingRequired?.wheelchair,
            completed: false,
            status: job.info.status
          });
        }

        if (newWaypoints.length > 0) {
          setJobDetails(prev => {
            const tk = tripKey(tripNumber);
            const filtered = prev.filter(wp => tripKey(wp.jobNumber) !== tk);
            const merged = [...filtered, ...newWaypoints];
            return sortWaypointsDrivingOrder(merged);
          });

          // Update current trip and vehicle status
          const activeJob = {
            tripNumber: job.info.jobNumber,
            status: job.info.status,
            nextEventType: newWaypoints[0].type
          };
          setCurrentTrip(activeJob);
          comms.setCurrentTripStatus(activeJob);
          const st = String(job.info.status || '').trim().toLowerCase();
          if (st === 'loaded' || st === 'arriveddestination') {
            setVehicleStatus('Loaded');
            comms.setCurrentVehicleStatus('Loaded');
          } else if (st === 'enroute') {
            setVehicleStatus('Enroute');
            comms.setCurrentVehicleStatus('Enroute');
          } else {
            setVehicleStatus('Dispatched');
            comms.setCurrentVehicleStatus('Dispatched');
          }
        }

        if (String(job.info.status || '').trim().toLowerCase() === 'loaded') {
          const tk = tripKey(job.info.jobNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
            guardedStartMeter(job.info.jobNumber, p.meter, p.wait);
          }
        }
      } catch (e) {
        console.error('Error parsing JOBM job details:', e);
      }
    },
    
    /**
     * JOBMF after FLAGM — same job JSON as JOBM. Process via onJobDetails to merge
     * trips/pricing, then resolve any pending FlagTripModal awaiter with this jobNumber
     * (Phase 1.3 — JOBMF is the canonical FLAGM ack, not STARTM).
     */
    onFlagTrip: (tripNumber, jobText) => {
      const fn = socketCallbacksRef.current.onJobDetails;
      if (typeof fn === 'function') {
        fn(tripNumber, jobText);
      }
      const resolver = pendingFlagAckRef.current;
      if (typeof resolver === 'function') {
        pendingFlagAckRef.current = null;
        try {
          resolver(tripNumber);
        } catch (err) {
          console.error('FlagTripModal pending ack resolver threw:', err);
        }
      }
    },

    onDeleteJob: (tripNumber) => {
      const tk = tripKey(tripNumber);
      setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== tk));
      if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
        // Bind stop to this jobNumber — no-op if meter is running for a different trip.
        stopMeter(tripNumber);
      }
      delete tripPricingRef.current[tk];
    },
    
    onSetDriver: (driverData) => {
      setDriverDetails(driverData);

      if (driverData && driverData.fixedRoute) {
        // Driver has fixed route — redirection handled elsewhere; bail out of the
        // schedule break processing path.
        return;
      }
      
      // Process route break information if present
      if (driverData.route && driverData.route.BreakStart && driverData.route.BreakEnd) {
        const breakStart = new Date(driverData.route.BreakStart);
        const breakEnd = new Date(driverData.route.BreakEnd);
        const currentTime = new Date();
        
        // Only add break to itinerary if it hasn't already ended
        if (currentTime < breakEnd) {
          // Create break waypoint object
          const breakWaypoint = {
            type: 'Break',
            timeStamp: breakStart.getTime() / 1000, // Convert to Unix timestamp
            time: breakStart.toLocaleTimeString(),
            startTime: breakStart.toLocaleTimeString(),
            endTime: breakEnd.toLocaleTimeString(),
            paxName: 'Driver Break',
            jobNumber: 'BREAK',
            address1: `Break: ${breakStart.toLocaleTimeString()} - ${breakEnd.toLocaleTimeString()}`,
            address2: 'Scheduled Break Time',
            account: 'System',
            phone: '',
            wheelChair: false,
            completed: false,
            status: 'Scheduled',
            isBreak: true,
            location: [0, 0] // No specific location for break
          };
          
          // Insert break into existing job details at correct time position
          setJobDetails(prevJobs => {
            const updatedJobs = [...prevJobs, breakWaypoint];
            return sortWaypointsDrivingOrder(updatedJobs);
          });
        }
      }
    },
    
    onSetInAppPayment: (tripNumber) => {
      setPaymentModal({ show: true, tripNumber });
    },
    
    onSetLoaded: (tripNumber) => {
      const tk = tripKey(tripNumber);
      setCurrentTrip((prev) => {
        if (!prev || tripKey(prev.tripNumber) !== tk) return prev;
        const next = { ...prev, status: 'Loaded' };
        comms.setCurrentTripStatus(next);
        return next;
      });
      const p = tripPricingRef.current[tk];
      if (p?.meter && hasUsableMeterRates(p.meter) && !meterRunningRef.current) {
        guardedStartMeter(tripNumber, p.meter, p.wait);
      }
    },
    
    onSetCompleted: (tripNumber) => {
      // Bind stop to this jobNumber so a stray COMPM for a different trip never
      // interrupts the active fare (Phase 3.10).
      stopMeter(tripNumber);
      const tk = tripKey(tripNumber);
      delete tripPricingRef.current[tk];
      if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
        setCurrentTrip(null);
        comms.setCurrentTripStatus(null);
        setVehicleStatus('Clear');
        comms.setCurrentVehicleStatus('Clear');
      }
    }
  };

  // Keep the ref synced with the latest callbacks on every render
  // so socket handlers always call current closures (no stale state)
  socketCallbacksRef.current = socketCallbacks;

  useEffect(() => {
    if (!userSettings) {
      // User settings are passed as props, no need to load from localStorage
      return;
    }
  }, [userSettings]);

  useEffect(() => {
    if (!fleetSettings) return;
    const mapKey =
      fleetSettings.googleApiKey ||
      fleetSettings.googleAPIKey ||
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      null;
    setGoogleApiKey(mapKey || null);
  }, [fleetSettings]);

  useEffect(() => {
    const initialize = async () => {
      if (!fleetSettings) return;

      let localSocket =null;
      
      // Build a stable proxy that delegates to socketCallbacksRef.current
      // This way socket handlers always call the latest closures, not stale ones
      const stableCallbacks = new Proxy({}, {
        get: (target, prop) => {
          const fn = socketCallbacksRef.current[prop];
          if (typeof fn === 'function') {
            return (...args) => fn(...args);
          }
          return fn;
        },
        ownKeys: () => Object.keys(socketCallbacksRef.current),
        getOwnPropertyDescriptor: (target, prop) => {
          if (prop in socketCallbacksRef.current) {
            return { configurable: true, enumerable: true, writable: true, value: socketCallbacksRef.current[prop] };
          }
        },
        has: (target, prop) => prop in socketCallbacksRef.current
      });
      
      // Pass socket callbacks to initializeSocket instead of setting up here
      const cleanup = await initializeSocket(localSocket, setJobDetails, setDriverDetails, userSettings, fleetSettings, stableCallbacks);
      return cleanup;
    };

    initialize();
  }, [ fleetSettings, userSettings]);

  const displayTripInfo = (jobNumber) => {
    const trip = jobDetails.filter((job) => job.jobNumber === jobNumber);
    setSelectedTrip(trip);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTrip(null);
  };

  const geolocationSuccess = (position) => {
    const { latitude, longitude } = position.coords;
    const normalized =
      readGoogleLatLng({ lat: latitude, lng: longitude }) ??
      normalizeMapLatLng({ lat: latitude, lng: longitude });
    if (normalized) setCenter(normalized);
  };

  const handleDirectionsCalculated = (durationSeconds, meta = {}) => {
    if (jobDetails[0]) {
      const now = new Date();
      const eta = new Date(now.getTime() + durationSeconds * 1000);
      setCurrentEta(eta.toLocaleTimeString());
      const minutes = Math.max(1, Math.round(durationSeconds / 60));
      setRouteDriveSummary({
        minutes,
        usesTraffic: !!meta.usedTrafficDuration,
        distanceMeters:
          typeof meta.distanceMeters === 'number' && meta.distanceMeters > 0
            ? meta.distanceMeters
            : null,
        routePreference:
          meta.routePreference === 'shortest' ? 'shortest' : 'fastest',
      });
    }
  };

  const activeJobNumber = jobDetails[0]?.jobNumber;
  useEffect(() => {
    setRouteDriveSummary(null);
    setCurrentEta(null);
  }, [activeJobNumber]);

  const handleRequestDirections = (requestCallback, showDirectionsCallback) => {
    const vehicleOrigin = readGoogleLatLng(center) ?? normalizeMapLatLng(center);
    const overrideDestination = normalizeMapLatLng(softMeterRouteOverride?.destination);
    if (
      meterRunning &&
      vehicleOrigin &&
      overrideDestination &&
      isValidMapPosition(vehicleOrigin) &&
      isValidMapPosition(overrideDestination)
    ) {
      requestCallback({
        origin: vehicleOrigin,
        destination: overrideDestination,
        travelMode: 'DRIVING',
      });
      showDirectionsCallback(true);
      return;
    }

    const origin = normalizeMapLatLng(center);
    if (jobDetails.length > 0 && origin && isAccurate) {
      // Build the complete itinerary starting with vehicle location
      const waypoints = [];
      
      // Add all waypoints from jobDetails in order - only include valid DirectionsWaypoint properties
      jobDetails.forEach((job) => {
        if (!Array.isArray(job.location) || job.location.length < 2) return;
        const loc = normalizeMapLatLng({
          lat: job.location[0],
          lng: job.location[1],
        });
        if (!loc) return;
        waypoints.push({
          location: loc,
          stopover: true,
        });
      });

      if (waypoints.length === 0) {
        console.warn('LiveDispatch: directions skipped — no valid waypoint coordinates');
        return;
      }

      // OSRM-only routing for cumulative ETAs across all jobs. Returns null on any
      // failure (no osrmUrl, network error, empty result) so the UI silently keeps
      // its previous ETA values.
      const osrmUrl =
        fleetSettings?.osrmUrl ||
        fleetSettings?.osrmBaseUrl ||
        fleetSettings?.OsrmBaseUrl ||
        null;

      // Issue one OSRM request per job to get per-leg distance/duration. This matches
      // the prior Google semantics where each waypoint produced its own leg. Failures
      // (null result) leave the corresponding job ETA at zero so cumulatives still flow.
      const legPromises = [];
      let prevPoint = origin;
      for (const wp of waypoints) {
        legPromises.push(
          getDirections({ origin: prevPoint, destination: wp.location, osrmUrl })
        );
        prevPoint = wp.location;
      }

      Promise.all(legPromises)
        .then((legResults) => {
          if (!legResults.some((r) => r && r.path && r.path.length > 0)) return;

          const updatedJobDetails = [...jobDetails];
          let cumulativeDistance = 0;
          let cumulativeDuration = 0;

          legResults.forEach((leg, legIndex) => {
            if (legIndex >= updatedJobDetails.length) return;

            if (leg && typeof leg.distance === 'number' && typeof leg.duration === 'number') {
              const distanceMeters = leg.distance;
              const durationSeconds = leg.duration;
              const distanceMiles = (distanceMeters * 0.000621371).toFixed(2);
              const durationMinutes = Math.round(durationSeconds / 60);

              cumulativeDistance += distanceMeters;
              cumulativeDuration += durationSeconds;

              updatedJobDetails[legIndex] = {
                ...updatedJobDetails[legIndex],
                etaDistance: distanceMiles,
                etaDuration: durationMinutes,
                cumulativeDistance: (cumulativeDistance * 0.000621371).toFixed(2),
                cumulativeDuration: Math.round(cumulativeDuration / 60),
                estimatedArrival: new Date(Date.now() + cumulativeDuration * 1000).toLocaleTimeString(),
              };
            } else {
              // Co-located stop or OSRM failed for this leg — assume zero-length leg so
              // cumulative values still progress correctly.
              updatedJobDetails[legIndex] = {
                ...updatedJobDetails[legIndex],
                etaDistance: '0.00',
                etaDuration: 0,
                cumulativeDistance: (cumulativeDistance * 0.000621371).toFixed(2),
                cumulativeDuration: Math.round(cumulativeDuration / 60),
                estimatedArrival: new Date(Date.now() + cumulativeDuration * 1000).toLocaleTimeString(),
              };
            }
          });

          setJobDetails(updatedJobDetails);
        })
        .catch(() => {
          // Silent failure — keep existing ETAs.
        });

      // Trigger the map polyline display for the first segment (vehicle -> first stop)
      const displayRequest = {
        origin,
        destination: waypoints[0].location,
        travelMode: 'DRIVING',
      };
      requestCallback(displayRequest);
      showDirectionsCallback(true);
    } else {
      console.error('Cannot request directions: Missing waypoints, valid GPS center, or accuracy.');
    }
  };

  // Dispatch action handlers
  const handleAcceptTrip = () => {
    if (pendingOffer) {
      // Cancel auto-accept timer since driver is manually accepting
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
        jobOfferTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      comms.acceptTrip(pendingOffer.tripNumber);
      setShowJobOffer(false);
      setPendingOffer(null);
      setOfferCountdown(0);
    }
  };

  const handleRejectTrip = () => {
    if (pendingOffer) {
      // Cancel auto-accept timer since driver is manually rejecting
      if (jobOfferTimerRef.current) {
        clearTimeout(jobOfferTimerRef.current);
        jobOfferTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      comms.rejectTrip(pendingOffer.tripNumber);
      setShowJobOffer(false);
      setPendingOffer(null);
      setOfferCountdown(0);
    }
  };

  const handleTripStatusChange = (tripNumber, newStatus) => {
    switch (newStatus) {
      case 'Enroute':
        comms.setEnroute(tripNumber);
        break;
      case 'ArrivedOrigin':
        comms.setArrivedOrigin(tripNumber);
        break;
      case 'Loaded':
        comms.setLoaded(tripNumber);
        {
          const tk = tripKey(tripNumber);
          const p = tripPricingRef.current[tk];
          if (p?.meter && hasUsableMeterRates(p.meter)) {
            guardedStartMeter(tripNumber, p.meter, p.wait);
          } else {
            console.warn(
              'SoftMeter: no usable meter config for trip',
              tripNumber,
              '- ensure STARTM/JOBM includes meter rates; meter will start automatically when rates arrive.'
            );
          }
        }
        break;
      case 'ArrivedDestination':
        comms.setArrivedDestination(tripNumber);
        break;
      case 'NoShow':
        comms.setNoShow(tripNumber);
        stopMeter(tripNumber);
        break;
      case 'Completed': {
        if (meterRunning) {
          // Pause (don't stop) the meter and show fare confirmation modal.
          // pauseMeter keeps meterRef.current alive so handleFareCancel → reEnableMeter
          // can resume the same meter. handleFareConfirm calls stopMeter to finalize.
          const fareData = getFareBreakdown();
          const stats = getMeterStats();
          pauseMeter(tripNumber);
          if (fareData) {
            setFareConfirmModal({ show: true, tripNumber, fareData, stats });
            return; // Don't update trip state yet — modal handles it
          }
        }
        comms.setCompleted(tripNumber);
        break;
      }
      default:
        console.warn('Unknown trip status:', newStatus);
    }

    setCurrentTrip((prev) => {
      if (!prev || tripKey(prev.tripNumber) !== tripKey(tripNumber)) {
        return prev;
      }
      const next = { ...prev, status: newStatus };
      comms.setCurrentTripStatus(next);
      return next;
    });
  };

  const handleVehicleStatusChange = (newStatus) => {
    switch (newStatus) {
      case 'Busy':
        comms.setBusy();
        break;
      case 'Clear':
        comms.setClear();
        break;
      case 'Logoff':
        comms.logoff();
        break;
      default:
        console.warn('Unknown vehicle status:', newStatus);
    }
  };

  const handleShowMessages = () => {
    setShowMessages(true);
    stopFlashing(); // Stop flashing when modal opens
  };

  const handleCloseMessages = () => {
    setShowMessages(false);
  };

  const handleSendMessage = (messageText) => {
    comms.sendMessage(messageText);
    
    // Add to local messages
    const newMessage = {
      id: Date.now(),
      text: messageText,
      timestamp: new Date(),
      type: 'sent',
      from: driverDetails?.name || 'Driver'
    };
    
    setMessages(prev => [...prev, newMessage]);
  };

  const handleDismissScheduleToast = () => {
    setShowScheduleToast(false);
    toastCooldownRef.current = true;
    
    // Reset cooldown after 5 seconds
    setTimeout(() => {
      toastCooldownRef.current = false;
    }, 5000);
  };

  const handleMarkAsRead = () => {
    setHasUnreadMessages(false);
    stopFlashing();
  };

  const handleWaypointAction = (action, jobNumber, waypointType) => {
    switch (action) {
      case 'arrive':
        if (waypointType === 'Dropoff') {
          handleTripStatusChange(jobNumber, 'ArrivedDestination');
        } else {
          handleTripStatusChange(jobNumber, 'ArrivedOrigin');
        }
        break;
      case 'load':
        handleTripStatusChange(jobNumber, 'Loaded');
        // Remove the completed pickup waypoint from jobDetails (case-insensitive type)
        {
          const jk = tripKey(jobNumber);
          setJobDetails((prev) =>
            sortWaypointsDrivingOrder(
              prev.filter(
                (job) =>
                  !(tripKey(job.jobNumber) === jk && waypointTypeKey(job.type) === 'pickup')
              )
            )
          );
        }
        break;
      case 'complete':
        handleTripStatusChange(jobNumber, 'Completed');
        // Waypoint removal happens in handleTripStatusChange (immediate)
        // or handleFareConfirm (metered trip after modal)
        if (!meterRunning) {
          const jk = tripKey(jobNumber);
          setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== jk));
        }
        break;
      case 'noshow':
        handleTripStatusChange(jobNumber, 'NoShow');
        {
          const jk = tripKey(jobNumber);
          setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== jk));
        }
        break;
      case 'callout':
        comms.triggerCallout(jobNumber);
        break;
      default:
        console.warn('Unknown waypoint action:', action);
    }
  };

  const handlePaymentUpdate = (paymentType) => {
    if (paymentModal.tripNumber) {
      comms.updatePayment(paymentModal.tripNumber, paymentType);
      setPaymentModal({ show: false, tripNumber: null });
    }
  };

  /**
   * Driver confirmed the fare. Build PRICE line items for meter, wait, and any each-typed
   * extras (with driver-adjusted qty). Tip / toll / surcharge ride alongside as a separate
   * disposition block so the server records them in disposition.price.* columns rather than
   * as ad-hoc pricing rows (Phase 5.21).
   *
   * @param {number} adjustedTotal       Total the driver confirmed (may equal calculated total).
   * @param {object} [dispositionUpdates] `{ tip, toll, surcharge }` from the modal.
   * @param {Array}  [usedExtras]         Extras the driver applied (qty > 0).
   */
  const handleFareConfirm = (adjustedTotal, dispositionUpdates = null, usedExtras = []) => {
    const { tripNumber, fareData, stats } = fareConfirmModal;
    const pricingData = tripPricingRef.current[tripKey(tripNumber)];

    const priceItems = [];
    if (pricingData?.meter) {
      priceItems.push({
        id: pricingData.meter.id || '',
        rate: adjustedTotal,
        name: 'Meter',
        type: 'meter',
      });
    }
    if (fareData?.waitCharge > 0 && pricingData?.wait) {
      // Server's PRICE handler (lib/dispatch.js jobUpdatePriceItems) zeroes out any
      // flat-rate (no-qty) item whose name contains "Wait". To bypass that branch we
      // send qty so the server takes the qty path: TripAmount = ItemAmount * qty.
      // Since ItemAmount on the server == our wait.timeUnitRate (same Rate_RateItem
      // source), qty = waitCharge / timeUnitRate yields TripAmount ≈ waitCharge.
      // Round qty to 4 decimals so the inline SQL doesn't pick up float noise like
      // "22.500000000000004" from divisions like 6.75 / 0.30.
      const unitRate = Number(pricingData.wait.timeUnitRate) || 0;
      const rawQty = unitRate > 0 ? fareData.waitCharge / unitRate : 1;
      const qty = Math.round(rawQty * 10000) / 10000;
      priceItems.push({
        id: pricingData.wait.id || '',
        rate: fareData.waitCharge,
        qty,
        name: 'Wait Time',
        type: 'wait',
      });
    }
    if (Array.isArray(usedExtras)) {
      for (const ex of usedExtras) {
        const qty = Number(ex?.qty);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        priceItems.push({
          id: ex.id || '',
          rate: Number(ex.rate) || 0,
          qty,
          name: ex.name || 'Extra',
          type: 'each',
        });
      }
    }

    if (stats) {
      comms.sendMeterDistance(tripNumber, stats.totalDistance);
    }
    comms.completeWithPricing(tripNumber, priceItems, dispositionUpdates);

    // Now that the trip is being completed for real, fully tear down the meter
    // (was only paused before the modal so Cancel could resume it).
    stopMeter(tripNumber);

    setFareConfirmModal({ show: false, tripNumber: null, fareData: null, stats: null });
    const tk = tripKey(tripNumber);
    delete tripPricingRef.current[tk];
    setJobDetails(prev => prev.filter(job => tripKey(job.jobNumber) !== tk));
    if (currentTrip && tripKey(currentTrip.tripNumber) === tk) {
      setCurrentTrip(null);
      comms.setCurrentTripStatus(null);
      setVehicleStatus('Clear');
      comms.setCurrentVehicleStatus('Clear');
    }
  };

  const handleFareCancel = () => {
    const tn = fareConfirmModal?.tripNumber;
    reEnableMeter(tn);
    setFareConfirmModal({ show: false, tripNumber: null, fareData: null, stats: null });
  };

  // Soft meter state object passed down to CurrentWaypoint via WaypointMap
  const softMeterState = meterRunning ? {
    fare: meterFare,
    fareMode: meterFareMode,
    distance: meterDistance,
    isRunning: meterRunning,
    elapsedTime: meterElapsedTime,
    fareTimeSeconds: meterFareTimeSeconds,
    waitTime: meterWaitTime,
    waitCharge: meterWaitCharge,
    distanceCharge: meterDistanceCharge,
    timeCharge: meterTimeCharge,
    flagRate: Number(meterMeterConfig?.flagRate) || 0,
  } : null;

  const firstWp = jobDetails[0];
  const firstTripKey = firstWp ? tripKey(firstWp.jobNumber) : '';
  const firstTripPricing = firstTripKey ? tripPricingRef.current[firstTripKey] : null;
  const meterRatesReady =
    !!(firstTripPricing?.meter && hasUsableMeterRates(firstTripPricing.meter));

  // For desktop/tablet, we show two columns; for phone, the existing stacked layout.
  const containerClass = isPhone
    ? 'flex flex-col' // phone: stacked layout
    : 'flex flex-row h-full'; // desktop: side-by-side layout filling height

  const mapSectionStyle = isPhone
    ? { padding: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }
    : { padding: '5px', flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const itinerarySectionStyle = isPhone
    ? { width: '100%' }
    : { flex: '1', overflowY: 'auto' };

  return (
      <div className="bg-slate-100" style={{height: `calc(100vh - 65px)`}}>
        <GeolocationHandler
          onLocationUpdate={handleLocationUpdate}
          setIsAccurate={setIsAccurate}
          onRawPosition={meterLocationUpdate}
          meterActive={meterRunning}
        />
        
        {/* Vehicle Status Bar - moved to bottom with trip integration */}
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-2 z-50">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-sm">
                Status: {currentTrip ? currentTrip.status : vehicleStatus}
                {currentTrip && <span className="ml-2">Trip {currentTrip.tripNumber}</span>}
              </span>
              {driverDetails && (
                <span className="text-xs text-blue-200">
                  {driverDetails.number}: {driverDetails.name}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {/* Trip status specific buttons */}
              {currentTrip ? (
                <>
                 
                </>
              ) : (
                /* Vehicle status buttons when no trip and status is Clear */
                <>
                  {vehicleStatus === 'Clear' && (
                    <button 
                      onClick={() => handleVehicleStatusChange('Busy')}
                      className="px-2 py-1 bg-red-500 text-xs rounded"
                    >
                      Busy
                    </button>
                  )}
                  {vehicleStatus === 'Busy' && (
                    <button 
                      onClick={() => handleVehicleStatusChange('Clear')}
                      className="px-2 py-1 bg-green-500 text-xs rounded"
                    >
                      Clear
                    </button>
                  )}
                </>
              )}
              
              {/* Messages Button with Flashing Animation */}
              <button 
                onClick={handleShowMessages}
                className={`px-2 py-1 text-xs rounded transition-colors duration-300 ${
                  isFlashing 
                    ? 'bg-orange-500 text-white' 
                    : hasUnreadMessages 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-blue-500 text-white'
                }`}
              >
                Messages{hasUnreadMessages && !showMessages && ' (!)'
                }
              </button>
            </div>
          </div>
        </div>

        {/* Messages Modal */}
        <Messages
          isOpen={showMessages}
          onClose={handleCloseMessages}
          vehicleNumber={driverDetails?.number}
          driverName={driverDetails?.name}
          messages={messages}
          onSendMessage={handleSendMessage}
          onMarkAsRead={handleMarkAsRead}
        />

        {center && (
          <div className={containerClass} style={{ marginTop: '0px', paddingBottom: '60px' }}>
            
            
            {/* Map section */}
            <div ref={mapContainerRef} style={mapSectionStyle}>
              <WaypointMap
                isPhone={isPhone}
                googleApiKey={googleApiKey}
                googleMapsLoaded={googleMapsLoaded}
                center={center}
                waypoint={jobDetails[0]}
                allWaypoints={jobDetails}
                vehicleOrientation={vehicleOrientation}
                vehicleStatus={vehicleStatus}
                expectedTime={currentEta}
                routeDriveSummary={routeDriveSummary}
                onDirectionsCalculated={handleDirectionsCalculated}
                isAccurate={isAccurate}
                handleRequestDirections={handleRequestDirections}
                onWaypointAction={handleWaypointAction}
                softMeterState={softMeterState}
                softMeterRouteOverride={softMeterRouteOverride}
                onSetSoftMeterRouteOverride={handleSetSoftMeterRouteOverride}
                onClearSoftMeterRouteOverride={handleClearSoftMeterRouteOverride}
                meterRatesReady={meterRatesReady}
                canFlag={canFlag}
                fareConfirmationOpen={
                  !!(fareConfirmModal.show && fareConfirmModal.fareData)
                }
                onFlagTripModalClosed={handleFlagTripModalClosed}
                onFlagTripModalOpen={onFlagTripModalOpen}
                onFlagTripModalDismiss={onFlagTripModalDismiss}
                registerPendingFlagAck={registerPendingFlagAck}
                fleetSettings={fleetSettings}
              />
            </div>
            
            {/* Itinerary section */}
            <div id="itinerary" className="flex flex-col" style={itinerarySectionStyle}>
              {jobDetails.length > 1 && (
                <Itinerary
                  isPhone={isPhone}      
                  jobs={jobDetails.slice(1).filter((job) => !job.completed)}
                  onTripSelect={displayTripInfo}
                />
              )}
            </div>
          </div>
        )}

        {/* Job Offer Modal */}
        {showJobOffer && pendingOffer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Trip Offer</h3>
              <div className="mb-4">
                <p><strong>Trip:</strong> {pendingOffer.tripNumber}</p>
                {pendingOffer.info && (
                  <>
                    <p><strong>Passenger:</strong> {pendingOffer.info.name}</p>
                    <p><strong>Account:</strong> {pendingOffer.info.account}</p>
                  </>
                )}
                {pendingOffer.locations && pendingOffer.locations[0] && (
                  <p><strong>Pickup:</strong> {pendingOffer.locations[0].landmark || `${pendingOffer.locations[0].street_num} ${pendingOffer.locations[0].street_name}`}</p>
                )}
                {pendingOffer.locations && pendingOffer.locations[1] && (
                  <p><strong>Dropoff:</strong> {pendingOffer.locations[1].landmark || `${pendingOffer.locations[1].street_num} ${pendingOffer.locations[1].street_name}`}</p>
                )}
                {pendingOffer.formattedTextTA && (
                  <div className="mt-2 p-2 bg-gray-100 rounded">
                    <div dangerouslySetInnerHTML={{ __html: pendingOffer.formattedTextTA }} />
                  </div>
                )}
              </div>
              {offerCountdown > 0 && (
                <div className="mb-3 text-center text-sm text-gray-500">
                  Auto-accepting in {offerCountdown}s
                </div>
              )}
              <div className="flex gap-4">
                <button 
                  onClick={handleAcceptTrip}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
                >
                  Accept
                </button>
                <button 
                  onClick={handleRejectTrip}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Message Input Modal */}
        {showMessageInput && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Send Message</h3>
              <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="w-full p-2 border rounded mb-4"
                rows="3"
                placeholder="Enter your message..."
              />
              <div className="flex gap-4">
                <button 
                  onClick={handleSendMessage}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Send
                </button>
                <button 
                  onClick={() => setShowMessageInput(false)}
                  className="flex-1 bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {paymentModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Payment Method</h3>
              <p className="mb-4">Select payment method for trip {paymentModal.tripNumber}:</p>
              <div className="flex flex-col gap-2">
                <button 
                  onClick={() => handlePaymentUpdate('CASH')}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
                >
                  Cash
                </button>
                <button 
                  onClick={() => handlePaymentUpdate('CREDIT')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Credit Card
                </button>
                <button 
                  onClick={() => handlePaymentUpdate('ACCOUNT')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700"
                >
                  Account
                </button>
                <button 
                  onClick={() => setPaymentModal({ show: false, tripNumber: null })}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fare modal only mounts when data exists so Total field initializes to calculated fare */}
        {fareConfirmModal.show && fareConfirmModal.fareData && (() => {
          const tk = fareConfirmModal.tripNumber
            ? tripKey(fareConfirmModal.tripNumber)
            : '';
          const pricingForTrip = tk ? tripPricingRef.current[tk] : null;
          const eachExtras = Array.isArray(pricingForTrip?.pricing)
            ? pricingForTrip.pricing.filter(
                (p) => String(p?.type || p?.Type || '').toLowerCase() === 'each'
              )
            : [];
          const dispPrice = pricingForTrip?.disposition?.price
            ?? pricingForTrip?.disposition?.Price
            ?? null;
          return (
            <FareConfirmationModal
              isOpen={fareConfirmModal.show}
              fareData={fareConfirmModal.fareData}
              extras={eachExtras}
              dispositionPrice={dispPrice}
              onConfirm={handleFareConfirm}
              onCancel={handleFareCancel}
            />
          );
        })()}

        <WaypointModal
          isOpen={isModalOpen}
          onClose={closeModal}
          waypoint={selectedTrip && selectedTrip[0]}
          allWaypoints={jobDetails}
          vehicleLocation={center}
          waypointIndex={selectedTrip && selectedTrip[0] ? 
            jobDetails.findIndex(wp => 
              wp.jobNumber === selectedTrip[0].jobNumber && 
              wp.type === selectedTrip[0].type
            ) : undefined
          }
          fleetSettings={fleetSettings}
        />

        {!isAccurate && (
          <GpsWarningTag onLocationUpdate={handleLocationUpdate} setIsAccurate={setIsAccurate} />
        )}

        {/* Schedule Update Toast */}
        {showScheduleToast && (
          <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center p-4">
            <div 
              className="bg-orange-500 text-white rounded-lg shadow-lg w-full max-w-md flex items-center justify-between p-4"
              style={{ height: '120px' }}
            >
              <div className="flex-1 flex items-center justify-center">
                <p className="text-lg font-semibold">Your schedule has been updated</p>
              </div>
              <button
                onClick={handleDismissScheduleToast}
                className="ml-4 bg-white text-orange-500 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors text-lg"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>
  );
}

export default LiveDispatch;
